import { formatCurrency, formatDate } from '@/lib/format.js';
import { Badge } from '@/components/ui/badge.jsx';
import Panel from './Panel.jsx';

// Shape:
//   { entries: [{ id, occurred_at, type, description, amount_in,
//                 amount_out, net_delta, status, voided, ... }],
//     totals: { ar_collected, ap_remitted, balance }, has_more }
const STATUS_VARIANT = {
  settled: 'success',
  pending: 'warning',
  open: 'info',
  voided: 'muted',
};

export default function TrustLedgerPanel({ ledger }) {
  if (!ledger) return null;
  const entries = ledger.entries || [];
  const totals = ledger.totals;

  return (
    <Panel
      title="Trust ledger"
      count={entries.length}
      empty={
        ledger.no_deals
          ? 'No deals associated with this account.'
          : entries.length === 0
            ? 'No ledger entries.'
            : null
      }
      footer={
        totals ? (
          <div className="font-mono text-[11px]">
            <div className="flex justify-between">
              <span className="text-text-dim">
                Collected {formatCurrency(totals.ar_collected)} · Remitted{' '}
                {formatCurrency(totals.ap_remitted)}
              </span>
              <span className={totals.balance === 0 ? 'text-text-dim' : 'text-text'}>
                Balance {formatCurrency(totals.balance)}
              </span>
            </div>
            {/* A rollup over an unknown term says so and names the rows
                (TRACON-MONEY-SPEC V6). A failed read is not an empty ledger. */}
            {totals.provisional ? (
              <div className="mt-1 text-danger">
                {totals.error
                  ? `Totals not available: ${totals.error}`
                  : `Provisional: ${totals.pending?.length || 0} settled row(s) carry no amount`}
              </div>
            ) : null}
          </div>
        ) : null
      }
    >
      <ul className="divide-y divide-border">
        {entries.map((e) => (
          <li
            key={e.id}
            className={`flex items-start justify-between gap-3 py-2 ${e.voided ? 'opacity-50' : ''}`}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-2 text-xs">
                <span className="text-text-dim">{formatDate(e.occurred_at)}</span>
                <Badge variant={STATUS_VARIANT[e.status] || 'outline'} className="text-[9px]">
                  {e.status?.toUpperCase()}
                </Badge>
              </div>
              <div className="mt-0.5 truncate text-sm text-text">{e.description}</div>
            </div>
            <div className="shrink-0 text-right font-mono text-sm">
              {e.amount_in ? (
                <div className="text-success">+{formatCurrency(e.amount_in)}</div>
              ) : null}
              {e.amount_out ? (
                <div className="text-accent">-{formatCurrency(e.amount_out)}</div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
